import React, { ForwardedRef, RefObject } from 'react';
import { Box, Grid, TableBody, TableHead, TableRow } from '@mui/material';
import {
  ProgramSummaryDialogTitle,
  ProgramSummaryDialogContent,
  ProgramSummaryDialogBox,
  ProgramSummaryCustomDialog,
  DataPointsCellHeader,
  DataPointsCellSubHeader,
  DataPointsCell,
  ScenarioName,
  DepartmentName,
  DeptSecondaryIdentifier,
  DialogTable,
} from 'spw-components/dialogs/total-program/styles';
import { InvisibleButton } from 'spw-shared/globalComponents';
import makeStyles from '@mui/styles/makeStyles';
import { colors } from 'spw-theme';
import {
  getKeyDataPointsParams,
  getScenarioValues,
  USF_BREAKDOWN_VALUES,
  PaperComponent,
} from 'spw-components/dialogs/total-program/utils';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import {
  applyOverflowingRulesToDefaultPosition,
  asUsf,
} from 'spw-shared/utils';
import { useSelector } from 'react-redux';
import { selectMeasurementSystem } from 'spw-redux/modules/user/selectors';
import { MeasurementSystem } from 'spw-components/unit-conversion/types';
import { PlainWorksheet } from '../../../models/Worksheet';

const useStyles = makeStyles({
  closeButton: {
    color: colors.mainBlue,
  },
});

interface UsfBreakdownDialogProps {
  anchorRef: ForwardedRef<HTMLElement>;
  showDialog: boolean;
  setShowDialog: (show: boolean) => void;
  totalProgramWorksheets: PlainWorksheet[];
  usf: number | string;
  usfPercentage: number | string;
  valueType: string;
  setProgramTotal: () => void;
  setCirculation: () => void;
  setPlanningFactor: () => void;
}

const VERTICAL_DIFF = 318;

const getTopLeft = (element: HTMLElement) => {
  return applyOverflowingRulesToDefaultPosition(
    (rect) => {
      const right = window.innerWidth - rect.left - rect.width;

      return {
        x: right + rect.width,
        y: rect.top - VERTICAL_DIFF,
      };
    },
    element,
    undefined,
    'right'
  );
};

const UsfBreakdownDialog: React.FC<UsfBreakdownDialogProps> = ({
  anchorRef,
  showDialog,
  setShowDialog,
  setProgramTotal,
  setCirculation,
  setPlanningFactor,
  totalProgramWorksheets,
  usf,
  usfPercentage,
  valueType,
}) => {
  const { getUnit, convertArea } = useUnitConversion();
  const measurementSystem = useSelector(selectMeasurementSystem);
  const classes = useStyles({});

  const { title } = getKeyDataPointsParams(
    valueType,
    getUnit('area'),
    convertArea
  );

  return (
    <ProgramSummaryCustomDialog
      open={showDialog}
      disablePortal
      disableEnforceFocus
      hideBackdrop
      PaperProps={{
        handle: '#draggable-usf-breakdown-dialog',
        component: PaperComponent,
      }}
      disableScrollLock
      anchorRef={anchorRef as RefObject<HTMLElement>}
      getTopLeft={getTopLeft}
    >
      <ProgramSummaryDialogTitle id="draggable-usf-breakdown-dialog">
        <Box display="flex">
          <Box alignItems="center" display="flex">
            SQUARE{' '}
            {measurementSystem === MeasurementSystem.metric
              ? 'METERS'
              : 'FOOTAGE'}{' '}
            BREAKDOWN
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            flexGrow="1"
          >
            <InvisibleButton
              className={classes.closeButton}
              onClick={() => {
                setShowDialog(false);
              }}
            >
              CLOSE
            </InvisibleButton>
          </Box>
        </Box>
      </ProgramSummaryDialogTitle>

      <ProgramSummaryDialogContent>
        <Grid container>
          <Grid item xs={4}>
            <ProgramSummaryDialogBox
              className={
                valueType === USF_BREAKDOWN_VALUES.PROGRAM_SUBTOTAL
                  ? 'selected'
                  : 'selectable'
              }
              onClick={setProgramTotal}
            >
              Subtotal
              <br />
              Program {getUnit('area')}
            </ProgramSummaryDialogBox>
          </Grid>
          <Grid item xs={4}>
            <ProgramSummaryDialogBox
              className={`center ${
                valueType === USF_BREAKDOWN_VALUES.CIRCULATION
                  ? 'selected'
                  : 'selectable'
              }`}
              onClick={setCirculation}
            >
              Circulation
            </ProgramSummaryDialogBox>
          </Grid>
          <Grid item xs={4}>
            <ProgramSummaryDialogBox
              className={
                valueType === USF_BREAKDOWN_VALUES.PLANNING_FACTOR
                  ? 'selected'
                  : 'selectable'
              }
              onClick={setPlanningFactor}
            >
              Planning
              <br />
              Factor
            </ProgramSummaryDialogBox>
          </Grid>
        </Grid>
        <DialogTable>
          <TableHead>
            <TableRow className="scenario-name-row">
              <DataPointsCellHeader width="60%">
                Scenario Name
              </DataPointsCellHeader>
            </TableRow>
            <TableRow className="department-name-row">
              <DataPointsCellHeader width="60%">
                Department Name
              </DataPointsCellHeader>
              <DataPointsCellHeader align="right" width="20%">
                {getUnit('area')}
              </DataPointsCellHeader>
              <DataPointsCellHeader align="right" width="20%">
                % {getUnit('area')}
              </DataPointsCellHeader>
            </TableRow>
            <TableRow>
              <DataPointsCellSubHeader>{title}</DataPointsCellSubHeader>
              <DataPointsCellSubHeader align="right">
                {asUsf(convertArea(usf))}
              </DataPointsCellSubHeader>
              <DataPointsCellSubHeader align="right">
                {usfPercentage}
              </DataPointsCellSubHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {totalProgramWorksheets.map((worksheet) => {
              const { scenario } = worksheet;
              const { department, name } = scenario;

              const { totalUsf, usfPercent } = getScenarioValues(
                worksheet,
                valueType
              );

              return (
                <TableRow key={`row-${scenario.id}`}>
                  <DataPointsCell>
                    <ScenarioName>{name}</ScenarioName>
                    <DepartmentName>{department.name}</DepartmentName>
                    <DeptSecondaryIdentifier>
                      {department.secondaryIdentifier}
                    </DeptSecondaryIdentifier>
                  </DataPointsCell>
                  <DataPointsCell align="right">
                    {asUsf(convertArea(totalUsf))}
                  </DataPointsCell>
                  <DataPointsCell align="right">{usfPercent}</DataPointsCell>
                </TableRow>
              );
            })}
          </TableBody>
        </DialogTable>
      </ProgramSummaryDialogContent>
    </ProgramSummaryCustomDialog>
  );
};

export default UsfBreakdownDialog;
